import { useState, useEffect, useCallback, useRef } from 'react';
import { getValueByStage } from '@/lib/appStage';

/**
 * API endpoints for address search and delivery checking
 */
const ADDRESS_SEARCH_API = getValueByStage({
  prod: "https://api.geo.trackeros.co/address-search",
  dev: "https://dev.api.geo.londongrade.com/address-search",
  local: "https://dev.api.geo.londongrade.com/address-search",
});

const DELIVERY_CHECK_API = getValueByStage({
  prod: "https://api.geo.trackeros.co/delivery-check",
  dev: "https://dev.api.geo.londongrade.com/delivery-check",
  local: "https://dev.api.geo.londongrade.com/delivery-check",
});

const TENANT_ID = 1; // Replenico tenant ID

export interface AddressSuggestion {
  id: string;
  displayName: string;
  latitude: number;
  longitude: number;
  postalCode?: string;
  city?: string;
  country?: string;
  formattedAddress?: string;
  street?: string;
  number?: string;
  county?: string;
}

export interface UseAddressSearchOptions {
  /** Debounce delay in milliseconds (default: 800ms) */
  debounceMs?: number;
  /** Minimum characters before searching (default: 3) */
  minSearchLength?: number;
}

export interface UseAddressSearchReturn {
  /** Current search query */
  query: string;
  /** Set the search query */
  setQuery: (value: string) => void;
  /** Address suggestions from search */
  suggestions: AddressSuggestion[];
  /** Whether search is in progress */
  isSearching: boolean;
  /** Whether the latest debounced search request has completed */
  hasResolvedSearch: boolean;
  /** Search error if any */
  searchError: string | null;
  /** Currently selected address */
  selectedAddress: AddressSuggestion | null;
  /** Set the selected address */
  selectAddress: (address: AddressSuggestion | null) => void;
  /** Check if selected address can be delivered to */
  checkDelivery: () => Promise<void>;
  /** Whether delivery is available */
  delivers: boolean | null;
  /** Whether delivery check is in progress */
  isCheckingDelivery: boolean;
  /** Delivery check error if any */
  deliveryError: string | null;
  /** Reset all state */
  reset: () => void;
}

/**
 * Custom hook for address search and delivery checking
 * 
 * Features:
 * - Debounced address search
 * - Autocomplete suggestions
 * - Delivery zone validation
 * - Clean error handling
 * 
 * @param options - Configuration options
 * @returns Address search state and methods
 * 
 * @example
 * ```tsx
 * const {
 *   query,
 *   setQuery,
 *   suggestions,
 *   isSearching,
 *   selectAddress,
 *   checkDelivery,
 *   delivers
 * } = useAddressSearch();
 * ```
 */
export function useAddressSearch(
  options: UseAddressSearchOptions = {}
): UseAddressSearchReturn {
  const { debounceMs = 800, minSearchLength = 3 } = options;

  // Search state
  const [query, setQueryInternal] = useState<string>('');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [hasResolvedSearch, setHasResolvedSearch] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Selection and delivery state
  const [selectedAddress, setSelectedAddress] = useState<AddressSuggestion | null>(null);
  const [delivers, setDelivers] = useState<boolean | null>(null);
  const [isCheckingDelivery, setIsCheckingDelivery] = useState<boolean>(false);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const searchAbortControllerRef = useRef<AbortController | null>(null);
  const deliveryAbortControllerRef = useRef<AbortController | null>(null);
  const latestSearchRequestRef = useRef(0);

  /**
   * Set query and reset suggestions when typing
   */
  const setQuery = useCallback((value: string) => {
    // Ensure value is always a string
    const stringValue = value ?? '';
    setQueryInternal(stringValue);
    setHasResolvedSearch(false);
    
    // Reset when query changes
    if (!stringValue.trim()) {
      setSuggestions([]);
      setSearchError(null);
      setSelectedAddress(null);
      setDelivers(null);
      setDeliveryError(null);
    }
  }, []);

  /**
   * Select an address from suggestions
   */
  const selectAddress = useCallback((address: AddressSuggestion | null) => {
    setSelectedAddress(address);
    setDelivers(null);
    setDeliveryError(null);
    
    if (address) {
      setQueryInternal(address.displayName);
      setSuggestions([]); // Clear suggestions when selected
    }
  }, []);

  /**
   * Search for addresses based on query
   */
  const searchAddresses = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < minSearchLength) {
      setSuggestions([]);
      setHasResolvedSearch(false);
      return;
    }

    // Cancel existing search
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }

    const requestId = latestSearchRequestRef.current + 1;
    latestSearchRequestRef.current = requestId;
    searchAbortControllerRef.current = new AbortController();
    setIsSearching(true);
    setHasResolvedSearch(false);
    setSearchError(null);

    try {
      const response = await fetch(
        `${ADDRESS_SEARCH_API}?query=${encodeURIComponent(searchQuery)}&tenant_id=${TENANT_ID}`,
        {
          signal: searchAbortControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        setSuggestions([]);
        setSearchError('Unable to search addresses. Please try again.');
        return;
      }

      const data = await response.json();
      
      // API returns a flat array of address objects
      const results = Array.isArray(data) ? data : (data.results || data.addresses || []);
      const addresses: AddressSuggestion[] = results.map((item: any, index: number) => ({
        id: item.id || `addr-${index}`,
        displayName: item.addressLabel || item.formattedAddress || item.formatted_address,
        latitude: item.latitude,
        longitude: item.longitude,
        postalCode: item.postalCode,
        city: item.city,
        country: item.country,
        formattedAddress: item.formattedAddress || item.addressLabel,
        street: item.street,
        number: item.number,
        county: item.county,
      }));

      setSuggestions(addresses);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      
      setSuggestions([]);
      setSearchError('Unable to search addresses. Please try again.');
    } finally {
      if (latestSearchRequestRef.current === requestId) {
        setIsSearching(false);
        setHasResolvedSearch(true);
      }
    }
  }, [minSearchLength]);

  /**
   * Check if delivery is available for selected address
   */
  const checkDelivery = useCallback(async () => {
    if (!selectedAddress) {
      setDeliveryError('Please select an address first');
      return;
    }

    // Cancel existing check
    if (deliveryAbortControllerRef.current) {
      deliveryAbortControllerRef.current.abort();
    }

    deliveryAbortControllerRef.current = new AbortController();
    setIsCheckingDelivery(true);
    setDeliveryError(null);
    setDelivers(null);

    try {
      const response = await fetch(
        `${DELIVERY_CHECK_API}?lat=${selectedAddress.latitude}&long=${selectedAddress.longitude}&tenant_id=${TENANT_ID}`,
        {
          signal: deliveryAbortControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        setDelivers(false);
        setDeliveryError('Unable to check delivery. Please try again.');
        return;
      }

      const data: { delivers: boolean } = await response.json();
      setDelivers(data.delivers);
      setDeliveryError(null);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      
      setDelivers(false);
      setDeliveryError('Unable to check delivery. Please try again.');
    } finally {
      setIsCheckingDelivery(false);
    }
  }, [selectedAddress]);

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    setQueryInternal('');
    setSuggestions([]);
    setIsSearching(false);
    setHasResolvedSearch(false);
    setSearchError(null);
    setSelectedAddress(null);
    setDelivers(null);
    setIsCheckingDelivery(false);
    setDeliveryError(null);

    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }
    if (deliveryAbortControllerRef.current) {
      deliveryAbortControllerRef.current.abort();
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
  }, []);

  /**
   * Debounced address search on query change
   */
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const trimmedQuery = (query ?? '').trim();

    if (trimmedQuery.length >= minSearchLength) {
      debounceTimerRef.current = setTimeout(() => {
        searchAddresses(trimmedQuery);
      }, debounceMs);
    } else {
      setSuggestions([]);
      setHasResolvedSearch(false);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [query, debounceMs, minSearchLength, searchAddresses]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
      }
      if (deliveryAbortControllerRef.current) {
        deliveryAbortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    query,
    setQuery,
    suggestions,
    isSearching,
    hasResolvedSearch,
    searchError,
    selectedAddress,
    selectAddress,
    checkDelivery,
    delivers,
    isCheckingDelivery,
    deliveryError,
    reset,
  };
}
